<script setup lang="ts">
/* Renders a <button> or, with `to`, a router link that looks identical.
   Variants and sizes are the only styling knobs — callers never reach
   for the CSS. Anything else (@click, aria-label, class) falls through. */
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import Icon from '@/components/Icon.vue'
import type { IconName } from '@/components/icons'

const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
    size?: 'sm' | 'md'
    icon?: IconName
    iconRight?: IconName
    /** Square, label-less button. `aria-label` becomes required in practice. */
    iconOnly?: boolean
    fullWidth?: boolean
    loading?: boolean
    disabled?: boolean
    /** Render as a router link instead of a button. */
    to?: string
    type?: 'button' | 'submit' | 'reset'
  }>(),
  {
    variant: 'primary',
    size: 'md',
    iconOnly: false,
    fullWidth: false,
    loading: false,
    disabled: false,
    type: 'button',
  },
)

const iconSize = computed(() => (props.size === 'sm' ? 16 : 18))
</script>

<template>
  <component
    :is="to ? RouterLink : 'button'"
    :to="to"
    :type="to ? undefined : type"
    :disabled="to ? undefined : disabled || loading"
    class="button"
    :class="[variant, size, { iconOnly, fullWidth, loading }]"
  >
    <span v-if="loading" class="spinner" aria-hidden="true" />
    <Icon v-else-if="icon" :name="icon" :size="iconSize" />
    <span v-if="$slots.default && !iconOnly" class="label"><slot /></span>
    <Icon v-if="iconRight" :name="iconRight" :size="iconSize" />
  </component>
</template>

<style scoped>
.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2xs);
  height: var(--control-height);
  padding-inline: var(--space-md);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  line-height: 1;
  letter-spacing: var(--tracking-wide);
  white-space: nowrap;
  text-decoration: none;
  border: var(--border-width) solid transparent;
  border-radius: var(--radius-sm);
  transition:
    background-color var(--dur-fast) var(--ease),
    border-color var(--dur-fast) var(--ease),
    color var(--dur-fast) var(--ease),
    box-shadow var(--dur-fast) var(--ease),
    transform var(--dur-fast) var(--ease);
}

.button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.button:not(:disabled):active {
  transform: translateY(1px);
}

/* --- variants ------------------------------------------------ */

.primary {
  color: var(--color-accent-contrast);
  background-color: var(--color-accent);
  box-shadow: var(--shadow-sm);
}

.primary:not(:disabled):hover {
  background-color: var(--color-accent-hover);
  box-shadow: var(--shadow-md);
}

.secondary {
  color: var(--color-heading);
  background-color: var(--color-surface);
  border-color: var(--color-border-strong);
}

.secondary:not(:disabled):hover {
  background-color: var(--color-surface-hover);
  border-color: var(--color-accent);
  color: var(--color-accent);
}

.ghost {
  color: var(--color-text-muted);
  background-color: transparent;
}

.ghost:not(:disabled):hover {
  color: var(--color-heading);
  background-color: var(--color-surface-sunken);
}

.danger {
  color: var(--color-danger);
  background-color: transparent;
  border-color: var(--color-border);
}

.danger:not(:disabled):hover {
  color: var(--color-accent-contrast);
  background-color: var(--color-danger);
  border-color: var(--color-danger);
}

/* --- sizes & modifiers --------------------------------------- */

.sm {
  height: var(--control-height-sm);
  padding-inline: var(--space-xs);
  font-size: var(--text-xs);
}

.iconOnly {
  padding-inline: 0;
  aspect-ratio: 1;
}

.fullWidth {
  width: 100%;
}

.label {
  overflow: hidden;
  text-overflow: ellipsis;
  /* The button is line-height 1 so its height is the control height and
     nothing else, but overflow:hidden clips to the line box -- and a 1.0 line
     box cuts off descenders: the tail of the g in "Sign in". The label gets
     the room back without changing the button's height. */
  line-height: var(--leading-snug);
}

/* --- loading spinner ----------------------------------------- */

.loading {
  cursor: progress;
}

.spinner {
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: var(--radius-full);
  opacity: 0.8;
  animation: spin 700ms linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
